(ns web3.components.parent
  (:require [web3.persistence.graphql :as g]
            [clojure.string :as str]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [web3.components.select :as select]
            [web3.components.text :as text]
            [web3.components.widgets :as widgets]
            [web3.modals.forms :as forms]
            [web3.persistence.current-property :as current-property]
            [web3.i18n :as i]
            [web3.routing.core :as routing]
            [web3.utils :as utils]
            [web3.view :as v]
            [web3.persistence.schema :as schema]
            [web3.routers.app-helpers :as app-route]
            [web3.queries.asset :as q-asset]
            [web3.icons.shapes :as shapes]))

;; ========== Helpers ==========

(def asset-name schema/asset-display-name)

(defn- parent-type*
  [id]
  (->> ["Asset" "AssetGroup" "Property"]
       (map #(g/local-typename (str % "-" id)))
       (filter (comp not nil?))
       first))

(defn fetch-parent
  [id]
  (case (parent-type* id)
    "Asset"
    (q-asset/fetch-asset id)
    "AssetGroup"
    (q-asset/fetch-group id)
    "Property"
    (current-property/get-property)
    nil))

(defn parent-type
  "Returns human-readable type for parent entity (must be an Asset, AssetGroup, or Property)"
  [id]
  (let [p-name (parent-type* id)]
    (condp = p-name
      "Asset" (i/get :type/Asset)
      "AssetGroup" (i/get :type/AssetGroup)
      "Property" (i/get :type/Property)
      nil nil)))

(defn parent-name
  "Returns human-readable name for parent entity (must be an Asset, AssetGroup, or Property)"
  [id]
  (let [{:keys [__typename] :as parent} (fetch-parent id)]
    (case __typename
      "Asset"
      (asset-name parent)
      "AssetGroup"
      (:name parent)
      "Property"
      (schema/entity-display-name parent)
      nil)))

;; ========== Props ==========
;;
;; Props are kept in `modal-state` to persist
;; across page/view transitions.

(defn props []
  (::props (forms/state)))

(defn reset-props! [props]
  (forms/swap-state! assoc ::props props))

(defn swap-props! [f & args]
  (apply forms/swap-state! update ::props f args))

(def context-defaults {:traverse-up? true
                       :traverse-down? true
                       :allowed-types #{"Asset"
                                        "AssetGroup"
                                        "Property"}
                       :mutable? true})

(defn modal-query-data

  [parent-context]
  ;;  :traverse-up?      -> otherwise will only look at current root & its children
  ;;  :traverse-down?    -> otherwise will not look at children of root
  ;;  :allowed-types #{} -> string __typename's of acceptable parents
  ;;  :mutable?          -> if no, sets :read-only? true on field
  ;;  :root              -> id of entity that the ^^above restrictions relate to
  {:form-modal-data {:parent-context parent-context}})

(defn get-parent-context
  "In `add` flows, we specify parent-context via a query param.
   In `edit` flows, we specify parent-context via props to `parent-selector`."
  []
  (let [{:keys [::props :query-data]} (forms/state)]
    (merge context-defaults
           (:parent-context query-data)
           (:parent-context props))))

(defn notify-change! [value]
  (let [{:keys [on-change change-value! on-save]} (props)]
    ;; NOTE
    ;; we can't rely on ordinary top-down controlled component flow,
    ;; because the `parent-selector` component is often _unmounted_
    ;; when this state changes. So we keep our own 'fake' copy of
    ;; props, which _should_ be equal to what we _would_ get if
    ;; our parent component was mounted.
    (swap-props! assoc :value value)

    ;; now we trigger the change handlers passed down as props.
    (when on-change
      (on-change #js {:target #js {:value value}}))
    (when change-value!
      (change-value! value))
    (when on-save
      (on-save value))))

(v/defview parent-selector
  {:component-will-mount
   (fn [{{:keys [value] :as props} :props}]
     (v/silently
      (reset-props! props)))
   :component-will-receive-props
   (fn [this [_ next-props]]
     (v/silently
      (reset-props! next-props)))}
  [{:keys [change-value! on-change value label read-only? on-save] :as props}]
  (assert (or change-value! on-change on-save) "`parent-selector` requires a `change-value!`, `on-change` or `on-save` prop.")
  (let [{:keys [mutable?]} (get-parent-context)
        read-only? (or read-only? (false? mutable?))
        cursor-class (if read-only? "cursor-default" "cursor-pointer")
        handle-tap (when-not read-only?
                     (fn [e]
                       (.preventDefault e)
                       (v/silently
                        (reset-props! props))
                       (rf/dispatch [:routing/nav (app-route/select-parent-href)])))]
    [text/input
     (v/merge-props
      (dissoc props
              :change-value!
              :on-change
              :select-asset?
              :parent-context)
      {:label (or label
                  (i/get :i/add-to))
       :key "parent-selector"
       :value (or (some-> value (parent-name))
                  "")
       :class cursor-class
       :read-only? read-only?
       :disabled true
       :container {:class
                   (str cursor-class " text-dark-gray hover-text-black-hover")
                   :on-click handle-tap
                   :on-touch-end handle-tap}
       :content/inline-right (when mutable?
                               shapes/chevron-right)})]))

;; ========== List ==========

(defn searchable? [s]
  (and (utils/some-str s)
       ;; (< 1 (count s))
       ))

(defn search-term
  []
  (routing/get-query :search))

(defn search!
  [term]
  (routing/set-query! :search term))

(defn match-search?
  [term {:keys [title]}]
  (if (and (searchable? term) title)
    (str/includes? (str/lower-case title) term)
    true))

(defn create-group! [this {:keys [name on-create property-id]
                           :or {property-id (current-property/get-id)}}]
  (-> {:name name
       :propertyId property-id}
      (q-asset/asset-group-create!
       {:component this
        :on-complete
        (fn [^js payload]
          (when on-create
            (-> payload
                utils/->clj-map
                (get-in [:data :asset-group-create :asset-group])
                on-create)))})))

(defn search
  [{:keys [on-create]}]
  (let [this (r/current-component)
        {:keys [graphql/loading? graphql/error]} (g/mutation-state)
        submit!
        (fn [e]
          (.preventDefault e)
          (when on-create
            (create-group! this {:name (search-term)
                                 :on-create on-create})))]
    [:form
     {:on-submit submit!}
     [text/input
      {:key "Search"
       :placeholder (i/get :select-parent/search-label)
       :mdc-container {:style {:background "#ededed"
                               :align-items "center"}
                       :class "mdc-textfield--no-underline group-create-input pad-right-2"}
       :value (search-term)
       :loading? loading?
       :on-change (comp search! utils/target-value)}]
     (when on-create
       [widgets/black-button
        {:on-click submit!
         :class "flex margin-v-3 width-100p"}
        (i/get :i/create-group)])]))

(defn parent-select-list
  [{:keys [items
           on-create]}]
  (let [term (some-> (search-term)
                     (str/lower-case))
        items (->> (doall
                    (for [{:keys [children
                                  expanded?]
                           :as item} items
                          :let [expanded? (or expanded?     ;; user manually expanded item
                                              ;; TODO
                                              ;; a way to set the _default_ expanded state
                                              ;; still allowing to collapse
                                              (seq (filter :selected? children))) ;; expand if group contains selected item
                                children (->> children
                                              (filter (fn [item]
                                                        (if (searchable? term)
                                                          (match-search? term item)
                                                          expanded?))))
                                children (->> children
                                              (map-indexed
                                               (fn [idx item]
                                                 (-> item
                                                     (update :class str
                                                             " child"
                                                             (when (-> (count children)
                                                                       (- 1)
                                                                       (not= idx))
                                                               " indent"))))))
                                match? (match-search? term item)]]
                      (cond
                        (seq children)
                        (-> item
                            (assoc :expanded? true)
                            (cons children))
                        match? [item]
                        :else [])))
                   (mapcat identity)
                   (map (v/partial-props select/selectable-dropdown {:nav-axis :vertical})))]
    [:div.parent-select-list.pad-all-3
     [:div.margin-bottom-3
      [search (when-not (seq items)
                {:on-create on-create})]]
     (seq items)]))